// =============================================================================
// pochodzeniePol — czysty rdzeń „skąd wiemy to, co piszemy o firmie".
//
// Zapobiega awarii z 13.09.2026 (Jan-Dar): `organization_profiles` mówił
// „firma handlowa, 10–50 pracowników" o piekarni z 1500 osób i NIKT nie umiał
// powiedzieć, które pole napisał wywiad, które rejestr, a które model wymyślił.
// Ekran „Profil firmy" pokazywał do tego „Brak danych o produktach" tam, gdzie
// nikt produktów nie badał — czyli werdykt tam, gdzie była niewiedza.
//
// Kształt `zrodla` jest DOKŁADNIE tym z migracji 689:
//   { "<pole>": { zrodlo, zrodlo_url?, pewnosc?, zapisano_at } }
// Trzy reguły z planu GENESIS_JEDNA_PRAWDA_PLAN.md §4.1, każda ma tu funkcję:
//   1. wpis człowieka wygrywa zawsze i nigdy nie jest nadpisywany automatem
//      (`czyChronione` — chroniony jest też wywiad, bo to człowiek mówił);
//   2. pole bez źródła I bez wartości to „nie zbadano", nie „brak"
//      (`czyNieZbadano` — jak „nie podaje ≠ 0" z panelu produkcyjnego);
//   3. etykieta pochodzenia jest jedna dla wszystkich ekranów (`etykietaZrodla`).
// Bez I/O — importują to komponenty renderowane w testach bez sieci.
// =============================================================================

export type ZrodloPola = 'rejestr_vat' | 'krs' | 'www' | 'wywiad' | 'synteza' | 'czlowiek';

export interface WpisZrodla {
  zrodlo: ZrodloPola;
  zrodlo_url?: string;
  pewnosc?: number;
  zapisano_at: string;
}

/** Pochodzenie pól profilu — klucz to nazwa pola (`mission`, `industry`…). */
export type Zrodla = Record<string, WpisZrodla>;

export const ZRODLA_POL: readonly ZrodloPola[] = [
  'rejestr_vat',
  'krs',
  'www',
  'wywiad',
  'synteza',
  'czlowiek',
];

/** Źródła, których automat (research, synteza) nie ma prawa nadpisać. */
const ZRODLA_CHRONIONE: ReadonlySet<ZrodloPola> = new Set<ZrodloPola>(['czlowiek', 'wywiad']);

const ETYKIETY: Record<ZrodloPola, string> = {
  rejestr_vat: 'z rejestru VAT',
  krs: 'z KRS',
  www: 'ze strony www',
  wywiad: 'z wywiadu',
  synteza: 'synteza Nexusa',
  czlowiek: 'wpis ręczny',
};

/** Jedna etykieta na wszystkie ekrany; nieznane źródło (np. z nowszej wersji
 *  serwera) wraca surowe, żeby nie udawać, że wiemy więcej niż wiemy. */
export function etykietaZrodla(zrodlo: ZrodloPola | string): string {
  return (ETYKIETY as Record<string, string>)[zrodlo] ?? zrodlo;
}

/** Czy `zrodla` ma poprawny wpis dla pola. */
export function wpisZrodla(zrodla: Zrodla | null | undefined, pole: string): WpisZrodla | null {
  const wpis = zrodla?.[pole];
  if (!wpis || typeof wpis !== 'object' || typeof wpis.zrodlo !== 'string') return null;
  return wpis;
}

function pusta(wartosc: unknown): boolean {
  if (wartosc === null || wartosc === undefined) return true;
  if (typeof wartosc === 'string') return wartosc.trim() === '';
  if (Array.isArray(wartosc)) return wartosc.length === 0;
  return false;
}

/**
 * „Nie zbadano" = brak wpisu w `zrodla` I pusta wartość. Pole z wartością bez
 * wpisu to profil sprzed jednej prawdy (kreator, research z ekranu) — ma treść,
 * więc pokazujemy treść, tylko bez etykiety. Pole ze wpisem i pustą wartością
 * to zbadane i puste (np. „firma nie ma strony www") — też nie „nie zbadano".
 */
export function czyNieZbadano(
  zrodla: Zrodla | null | undefined,
  pole: string,
  wartosc: unknown
): boolean {
  return wpisZrodla(zrodla, pole) === null && pusta(wartosc);
}

/** Pole, którego automat nie nadpisuje: człowiek albo wywiad. */
export function czyChronione(zrodla: Zrodla | null | undefined, pole: string): boolean {
  const wpis = wpisZrodla(zrodla, pole);
  return wpis !== null && ZRODLA_CHRONIONE.has(wpis.zrodlo);
}

/** Wpis „człowiek to napisał" — do zapisu z formularza. */
export function wpisCzlowieka(teraz: Date = new Date()): WpisZrodla {
  return { zrodlo: 'czlowiek', zapisano_at: teraz.toISOString() };
}

/**
 * Nowe `zrodla` po ręcznej edycji: pola, których wartość faktycznie się
 * zmieniła względem poprzedniego profilu, dostają wpis `czlowiek`; reszta
 * zostaje, jak była. Porównanie po wartości znormalizowanej (trim, null ≡ ''),
 * żeby otwarcie i zamknięcie formularza bez zmiany nie robiło z wywiadu wpisu
 * ręcznego.
 */
export function zrodlaPoEdycjiCzlowieka(
  poprzednie: Zrodla | null | undefined,
  przedtem: Record<string, unknown>,
  potem: Record<string, unknown>,
  teraz: Date = new Date()
): { zrodla: Zrodla; zmienione: string[] } {
  const zrodla: Zrodla = { ...(poprzednie ?? {}) };
  const zmienione: string[] = [];
  for (const pole of Object.keys(potem)) {
    if (!roznia(przedtem[pole], potem[pole])) continue;
    zrodla[pole] = wpisCzlowieka(teraz);
    zmienione.push(pole);
  }
  return { zrodla, zmienione };
}

function roznia(a: unknown, b: unknown): boolean {
  const na = pusta(a) ? '' : typeof a === 'string' ? a.trim() : JSON.stringify(a);
  const nb = pusta(b) ? '' : typeof b === 'string' ? b.trim() : JSON.stringify(b);
  return na !== nb;
}

/** Data z `zapisano_at` w formacie „13.09.2026"; pusta, gdy data nie parsuje. */
export function dataZapisu(wpis: WpisZrodla | null | undefined): string {
  if (!wpis?.zapisano_at) return '';
  const d = new Date(wpis.zapisano_at);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/**
 * Scalenie `applied` z researchu w profil: dopisuje TYLKO pola puste
 * i niechronione. Zwraca nowe wartości oraz listę pól, które faktycznie weszły
 * — reszta (wpisy człowieka, wywiad, pola już wypełnione) zostaje nietknięta.
 */
export function dopiszTylkoPuste<T extends Record<string, unknown>>(
  obecne: T,
  zrodla: Zrodla | null | undefined,
  applied: Record<string, unknown>,
  klucze: readonly string[]
): { wynik: T; dopisane: string[] } {
  const wynik: Record<string, unknown> = { ...obecne };
  const dopisane: string[] = [];
  for (const klucz of klucze) {
    const nowa = applied[klucz];
    if (pusta(nowa)) continue;
    if (czyChronione(zrodla, klucz)) continue;
    if (!pusta(obecne[klucz])) continue;
    wynik[klucz] = nowa;
    dopisane.push(klucz);
  }
  return { wynik: wynik as T, dopisane };
}
